import { ProjectAPI } from '@business/api/project';
import { UserId } from '@business/entities/account';
import { ContactId } from '@business/entities/contact';
import { OrgId, PaymentId } from '@business/entities/organization';
import {
  CompactProjectResponse,
  ProjectId,
  ProjectRequest,
  ProjectResponse,
  ProjectUpdateRequest,
  ProjectPermission,
  ProjectPermissionRequest,
  ProjectUserResponse,
} from '@business/entities/project';
import { ProjectSectionResponse, ProjectStepResponse } from '@business/entities/project/ProjectSectionResponse';
import { ProjectTemplateId } from '@business/entities/project/TemplateResponse';
import { handleMessage, requestMessage } from '@business/messages';
import { BaseBloc } from '@core/utils/bloc';
import { gcLabel } from '@core/utils/hooks/misc/gcLabel';
import { resolve } from '@core/utils/ioc';
import { R } from '@core/utils/r';
import { Repository } from '@core/utils/repository';
import { showDeleteConfirmation, showYesNo, showConfirmation } from '@modules/common';
import { map, switchMap, tap } from 'rxjs/operators';
import { Blocs } from './../tokens';
import { of } from 'rxjs';
import { PermissionService } from '@business/services/permission_service';
import { TaskPriority } from '@business/constants/task';
import { ProposalTemplateId } from '@business/entities/proposal';
import { InviteUsersFormData } from '@modules/projects/ProjectOverlay/InviteUserModal/InviteUserModal';
import { ClickupAPI } from '@business/api/clickup';
import { ClickupTaskRequest } from '@business/entities/clickup';

export class ProjectBloc extends BaseBloc {
  private readonly projectRepo = new Repository<CompactProjectResponse | ProjectResponse>({
    getItemId: project => project.id,
  });

  readonly projects$ = this.projectRepo.items$;
  readonly selectProject = this.projectRepo.selectItem;

  readonly selectStep = (projectId: ProjectId, stepId: string) =>
    this.projectRepo.selectItem(projectId).pipe(
      map(project => {
        if (!project) return undefined;
        const allSteps = R.flatMap((project as ProjectResponse).sections || [], section => section.steps);
        return allSteps.find(s => s.id === stepId);
      }),
    );

  onReset() {
    this.projectRepo.reset();
  }

  fetchProjects = () => {
    return ProjectAPI.fetchProjects().pipe(this.projectRepo.ops.reset(), handleMessage({ error: requestMessage('fetch_pros_error') }));
  };

  fetchProject = (id: ProjectId) => {
    return ProjectAPI.fetchProject(id).pipe(
      this.projectRepo.ops.upsertOne(item => ({ item })),
      handleMessage({ error: requestMessage('fetch_pros_error') }),
    );
  };

  createProject = (orgId: OrgId, project: ProjectRequest) => {
    return ProjectAPI.createProject(orgId, project).pipe(
      this.projectRepo.ops.addOne(item => item),
      handleMessage({
        type: requestMessage('create_project'),
      }),
    );
  };
  editProject = (orgId: OrgId, project: ProjectRequest) => {
    return ProjectAPI.editProject(orgId, project).pipe(
      this.projectRepo.ops.addOne(item => item),
      handleMessage({
        type: requestMessage('update_project'),
      }),
    );
  };

  updateProject = (id: ProjectId, project: ProjectUpdateRequest) => {
    return ProjectAPI.updateProject(id, project).pipe(
      this.projectRepo.ops.upsertOne(item => ({ item })),
      handleMessage({
        type: requestMessage('update_project'),
      }),
    );
  };

  getBrief = (id: ProjectId) => {
    return ProjectAPI.generateBrief(id).pipe(
      this.projectRepo.ops.upsertOne(item => ({ item })),
      handleMessage({
        type: requestMessage('update_project'),
      }),
    );
  };

  updateProjectUsers = (id: ProjectId, users: ProjectUserResponse[]) => {
    return ProjectAPI.updateProjectUsers(id, users).pipe(
      tap(projectUsers => {
        this.projectRepo.updateOne({
          id,
          changes: () => ({ users: projectUsers }),
        });
      }),
    );
  };

  createProjectUser = (id: ProjectId, user: InviteUsersFormData) => {
    return ProjectAPI.createProjectUser(id, user).pipe(this.projectRepo.ops.updateOne(users => ({ id, changes: () => ({ users }) })));
  };

  updateDueDate = (id: ProjectId, dueDate: number) => {
    return ProjectAPI.updateDueDate(id, dueDate).pipe(
      this.projectRepo.ops.upsertOne(item => ({ item })),
      handleMessage({
        type: requestMessage('update_project'),
      }),
    );
  };
  updateStartDate = (id: ProjectId, startDate: number) => {
    return ProjectAPI.updateStartDate(id, startDate).pipe(
      this.projectRepo.ops.upsertOne(item => ({ item })),
      handleMessage({
        type: requestMessage('update_project'),
      }),
    );
  };

  updateNotes = (id: ProjectId, notes: string) => {
    return ProjectAPI.updateNotes(id, notes).pipe(
      this.projectRepo.ops.updateOne(() => ({
        id,
        changes: () => ({ notes }),
      })),
    );
  };

  updateDescription = (id: ProjectId, description: string) => {
    return ProjectAPI.updateDescription(id, description).pipe(
      this.projectRepo.ops.updateOne(() => ({
        id,
        changes: () => ({ description }),
      })),
    );
  };

  updateBoard = (id: ProjectId, data: Pick<ProjectUpdateRequest, 'board' | 'stage'>) => {
    return ProjectAPI.updateBoard(id, data).pipe(
      this.projectRepo.ops.upsertOne(item => ({ item })),
      tap(p => resolve(Blocs.organization).updateProject(p.organization.id, p)),
      handleMessage({
        type: requestMessage('update_project'),
      }),
    );
  };

  deleteProject = (id: ProjectId) => {
    return showDeleteConfirmation(`Cancel ${gcLabel('Project')}`, `Do you really want to cancel this ${gcLabel('project')}?`).pipe(
      switchMap(() => ProjectAPI.deleteProject(id)),
      this.projectRepo.ops.removeOne(() => id),
      handleMessage({
        type: requestMessage('delete_project'),
      }),
    );
  };

  archiveProject = (id: ProjectId) => {
    return showDeleteConfirmation(`Archive ${gcLabel('Project')}`, `Do you really want to archive this ${gcLabel('project')}?`, {
      btnLabel: 'Confirm',
    }).pipe(
      switchMap(() => ProjectAPI.archiveProject(id)),
      this.projectRepo.ops.upsertOne(item => ({ item })),
      handleMessage({
        type: requestMessage('update_project'),
      }),
    );
  };

  unArchiveProject = (id: ProjectId) => {
    return showDeleteConfirmation(`Unarchive ${gcLabel('Project')}`, `Do you really want to unarchive this ${gcLabel('project')}?`, {
      btnLabel: 'Confirm',
    }).pipe(
      switchMap(() => ProjectAPI.archiveProject(id)),
      this.projectRepo.ops.upsertOne(item => ({ item })),
      handleMessage({
        type: requestMessage('update_project'),
      }),
    );
  };

  searchProjects = (search: string) => {
    return ProjectAPI.fetchProjects({ search }).pipe(handleMessage({ error: requestMessage('fetch_pros_error') }));
  };

  search = (search: string) => {
    return ProjectAPI.searchProjects({ search }).pipe(handleMessage({ error: requestMessage('fetch_pros_error') }));
  };

  updateProjectAction = (id: ProjectId, actionId: string, actionName: string) => {
    return ProjectAPI.updateProjectAction(id, actionId, actionName).pipe(
      this.projectRepo.ops.upsertOne(item => ({ item })),
      handleMessage({
        type: requestMessage('update_project'),
      }),
    );
  };

  sendInvoice = (id: ProjectId, emails?: string[], amount?: number, body?: string) => {
    return ProjectAPI.sendInvoice(id, emails, amount, body).pipe(
      handleMessage({
        type: requestMessage('project_invoice'),
      }),
    );
  };

  processPayment = (orgId: OrgId, id: ProjectId, paymentId: PaymentId, amount: number) => {
    return ProjectAPI.processPayment(id, paymentId, amount).pipe(
      tap(() => {
        resolve(Blocs.organization).fetchOrganization(orgId).subscribe();
      }),
      handleMessage({
        type: requestMessage('project_process_payment_cc'),
      }),
    );
  };

  manualPayment = (orgId: OrgId, id: ProjectId, paymentId: PaymentId, amount: number, paymentDate: number) => {
    return ProjectAPI.manualPayment(id, paymentId, amount, paymentDate).pipe(
      switchMap(() => {
        return resolve(Blocs.organization).fetchOrganization(orgId);
      }),
      handleMessage({
        type: requestMessage('project_process_payment'),
      }),
    );
  };

  setPriority = (projectId: ProjectId, priority: string) => {
    return ProjectAPI.setPriority(projectId, priority).pipe(
      this.projectRepo.ops.updateOne(item => ({
        id: projectId,
        changes: () => ({ priority: item.priority }),
      })),
    );
  };

  setStage = (projectId: ProjectId, stageId: number) => {
    return ProjectAPI.setStage(projectId, stageId).pipe(
      this.projectRepo.ops.updateOne(item => ({
        id: projectId,
        changes: () => ({ stageId: item.stageId }),
      })),
    );
  };

  selectManager = (projectId: ProjectId, userId: UserId) => {
    return ProjectAPI.selectManager(projectId, userId).pipe(
      this.projectRepo.ops.updateOne(item => ({
        id: projectId,
        changes: () => ({ accountManager: item.accountManager }),
      })),
    );
  };

  selectTemplate = (projectId: ProjectId, templateId: ProjectTemplateId) => {
    return ProjectAPI.selectTemplate(projectId, templateId).pipe(this.projectRepo.ops.upsertOne(item => ({ item })));
  };

  changeTemplate = (projectId: ProjectId, templateId: ProjectTemplateId) => {
    return ProjectAPI.changeTemplate(projectId, templateId).pipe(this.projectRepo.ops.upsertOne(item => ({ item })));
  };

  toggleStep = (projectId: ProjectId, stepId: number, completeAll?: boolean) => {
    return ProjectAPI.toggleStep(stepId, completeAll).pipe(
      this.projectRepo.ops.updateOne(section => ({
        id: projectId,
        changes: project => {
          const p = project as ProjectResponse;
          const sections = p.sections.map(s => {
            if (s.id == section.id) {
              return section;
            } else {
              return s;
            }
          });
          return { sections };
        },
      })),
    );
  };

  loadStep = (projectId: ProjectId, stepId: number) => {
    return ProjectAPI.loadStep(stepId).pipe(
      this.projectRepo.ops.updateOne(section => ({
        id: projectId,
        changes: project => {
          const p = project as ProjectResponse;
          const sections = p.sections.map(s => {
            if (s.id == section.id) {
              return section;
            } else {
              return s;
            }
          });
          return { sections };
        },
      })),
    );
  };

  assignStep = (projectId: ProjectId, stepId: string, data: Partial<ProjectStepResponse>) => {
    return ProjectAPI.assignStep(stepId, data).pipe(
      this.projectRepo.ops.updateOne(sections => ({
        id: projectId,
        changes: () => ({ sections }),
      })),
    );
  };

  /* Sections */
  createSection = (projectId: ProjectId, name: string, isPublic = false) => {
    return ProjectAPI.createSection(projectId, name, undefined, isPublic).pipe(
      this.projectRepo.ops.updateOne(sections => ({
        id: projectId,
        changes: () => ({ sections }),
      })),
      handleMessage({
        type: requestMessage('create_template_section'),
      }),
    );
  };

  updateSection = (projectId: ProjectId, sectionId: string, name: string, isPublic = false, orgProjId?: ProjectId) => {
    const oldProjectId = orgProjId || projectId;
    return ProjectAPI.updateSection(sectionId, name, undefined, isPublic, projectId).pipe(
      this.projectRepo.ops.updateOne(sections => {
        return {
          id: oldProjectId,
          changes: project => {
            return { sections: sections };
          },
        };
      }),
      handleMessage({
        type: requestMessage('update_template_section'),
      }),
    );
  };

  deleteSection = (projectId: ProjectId, id: string) => {
    return showDeleteConfirmation('Delete Section', 'Do you really want to remove this Section?').pipe(
      switchMap(() => ProjectAPI.deleteSection(id)),
      this.projectRepo.ops.updateOne(sections => ({
        id: projectId,
        changes: () => ({ sections }),
      })),

      handleMessage({
        type: requestMessage('delete_template_section'),
      }),
    );
  };

  duplicateSection = (projectId: ProjectId, id: string) => {
    return showConfirmation('Duplicate Section', 'Do you really want to duplicate this Section?').pipe(
      switchMap(() => ProjectAPI.duplicateSection(id)),
      this.projectRepo.ops.updateOne(sections => ({
        id: projectId,
        changes: () => ({ sections }),
      })),

      handleMessage({
        error: requestMessage('fetch_general_error'),
      }),
    );
  };

  duplicateStep = (projectId: ProjectId, id: string) => {
    return ProjectAPI.duplicateStep(id).pipe(
      this.projectRepo.ops.updateOne(sections => ({
        id: projectId,
        changes: () => ({ sections }),
      })),

      handleMessage({
        error: requestMessage('fetch_general_error'),
      }),
    );
  };

  sortSections = (projectId: ProjectId, sectionIds: string[]) => {
    return ProjectAPI.sortSections(sectionIds).pipe(
      tap(() => {
        const project = this.projectRepo.getItem(projectId)! as ProjectResponse;
        const newSections = project.sections.map(sec => {
          return { ...sec, menuOrder: R.findIndex(sectionIds, i => i === sec.id) };
        });

        this.projectRepo.updateOne({
          id: projectId,
          changes: () => ({ sections: R.sortBy(newSections, s => s.menuOrder) }),
        });
      }),
      handleMessage({
        error: requestMessage('update_template_section_sort'),
      }),
    );
  };

  assignSection = (projectId: ProjectId, sectionId: string, users: UserId[]) => {
    return ProjectAPI.assignSection(projectId, sectionId, users).pipe(
      this.projectRepo.ops.updateOne(sections => ({
        id: projectId,
        changes: () => ({ sections }),
      })),
    );
  };

  assignSectionUsers = (projectId: ProjectId, sectionId: string, users: UserId[]) => {
    return ProjectAPI.assignSectionUsers(projectId, sectionId, users).pipe(
      this.projectRepo.ops.updateOne(sections => ({
        id: projectId,
        changes: () => ({ sections }),
      })),
    );
  };

  /* Steps  */

  fetchStep = (projectId: ProjectId, id: string) => {
    return ProjectAPI.fetchStep(id).pipe(
      //   this.projectRepo.ops.updateOne(sections => ({
      //     id: projectId,
      //     changes: () => ({ sections }),
      //   })),
      handleMessage({
        error: requestMessage('fetch_step'),
      }),
    );
  };

  createStep = (projectId: ProjectId, sectionId: string, data: Partial<ProjectStepResponse>, parentStepId?: string) => {
    return ProjectAPI.createStep(sectionId, data, parentStepId, undefined, projectId).pipe(
      this.projectRepo.ops.updateOne(sections => ({
        id: projectId,
        changes: () => ({ sections }),
      })),

      handleMessage({
        error: requestMessage('create_template_section_step_error'),
      }),
    );
  };

  updateStep = (projectId: ProjectId, id: string, data: Partial<ProjectStepResponse>, success = false) => {
    return ProjectAPI.updateStep(id, data).pipe(
      this.projectRepo.ops.updateOne(section => ({
        id: projectId,
        changes: project => {
          const p = project as ProjectResponse;
          const sections = p.sections.map(s => {
            if (s.id == section.id) {
              return section;
            }
            return s;
          });
          return { sections };
        },
      })),

      success
        ? handleMessage({
            type: requestMessage('update_template_section_step'),
          })
        : handleMessage({
            error: requestMessage('save_step'),
          }),
    );
  };

  deleteStep = (projectId: ProjectId, id: string) => {
    return showDeleteConfirmation('Delete Step', 'Do you really want to remove this Step?').pipe(
      switchMap(() => ProjectAPI.deleteStep(id)),
      this.projectRepo.ops.updateOne(sections => ({
        id: projectId,
        changes: () => ({ sections }),
      })),

      handleMessage({
        type: requestMessage('delete_template_section_step'),
      }),
    );
  };

  sortSectionSteps = (projectId: ProjectId, sectionId: string, stepIds: string[], parentStepId?: string) => {
    return ProjectAPI.sortSectionSteps(stepIds).pipe(
      tap(() => {
        const project = this.projectRepo.getItem(projectId)! as ProjectResponse;

        const affectedSection = R.find(project.sections, s => s.id === sectionId)!;
        const newSteps = affectedSection.steps.map(step => {
          if (parentStepId) {
            const newSubSteps = step.subSteps.map(subStep => ({ ...subStep, menuOrder: R.findIndex(stepIds, i => i === subStep.id) }));
            return { ...step, subSteps: R.sortBy(newSubSteps, s => s.menuOrder) };
          }
          return { ...step, menuOrder: R.findIndex(stepIds, i => i === step.id) };
        });

        const newSections = project.sections.map(sec => {
          if (sec.id === sectionId) {
            return { ...sec, steps: R.sortBy(newSteps, s => s.menuOrder) };
          }
          return sec;
        });

        this.projectRepo.updateOne({
          id: projectId,
          changes: () => ({ sections: newSections }),
        });
      }),
      handleMessage({
        error: requestMessage('update_template_steps_sort'),
      }),
    );
  };

  markComplete = (projectId: ProjectId, completedAt: number) => {
    return ProjectAPI.markComplete(projectId, completedAt).pipe(
      this.projectRepo.ops.updateOne(() => ({
        id: projectId,
        changes: () => ({ completedAt: completedAt, status: 3 }),
      })),
    );
  };

  updateContactTimeZone = (projectId: ProjectId, contactId: ContactId, timeZone: string) => {
    return ProjectAPI.updateContactTimeZone(projectId, contactId, timeZone).pipe(
      this.projectRepo.ops.updateOne(contact => ({
        id: projectId,
        changes: () => ({ contact }),
      })),
      handleMessage({
        type: requestMessage('update_contact_timezone'),
      }),
    );
  };

  shareProject = (data: ProjectPermissionRequest, id: string) => {
    return showConfirmation('Share Project', PermissionService.getProjectPermissionMessage(data.permissionType)).pipe(
      switchMap(() => {
        return ProjectAPI.shareProject(data, id).pipe(this.projectRepo.ops.upsertOne(item => ({ item })));
      }),
      handleMessage({
        type: requestMessage('share_project'),
      }),
    );
  };

  ///////////////////////////

  sortProjectSections = (section: ProjectSectionResponse, fromPosition: any, toPosition: number, projectId: ProjectId) => {
    return ProjectAPI.sortProjectSections(section, fromPosition, toPosition, projectId).pipe(
      // this.projectRepo.ops.upsertMany(item => ({ item })),
      handleMessage({
        error: requestMessage('update_template_section_sort'),
      }),
    );
  };

  sortProjectSectionSteps = (step: ProjectStepResponse, toColumn: any, toPosition: number, projectId: ProjectId) => {
    // return ProjectAPI.sortProjectSectionSteps(step, toColumn, toPosition).pipe(
    //   handleMessage({
    //     error: requestMessage('update_template_steps_sort'),
    //   }),
    // );
    return ProjectAPI.sortProjectSectionSteps(step, toColumn, toPosition).pipe(
      tap(() => {
        const project = this.projectRepo.getItem(projectId)! as ProjectResponse;

        const affectedSection = R.find(project.sections, s => s.id === toColumn)!;
      }),
      handleMessage({
        error: requestMessage('update_template_steps_sort'),
      }),
    );
  };

  assignProjectStep = (projectId: ProjectId, stepId: string, data: string[]) => {
    return ProjectAPI.assignProjectStep(stepId, data).pipe(
      this.projectRepo.ops.updateOne(sections => ({
        id: projectId,
        changes: () => ({ sections }),
      })),
      handleMessage({
        error: requestMessage('update_template_steps_sort'),
      }),
    );
  };

  setProjectStepPriority = (priority: TaskPriority, stepId: string, projectId: ProjectId) => {
    return ProjectAPI.setProjectStepPriority(stepId, priority).pipe(
      this.projectRepo.ops.updateOne(sections => ({
        id: projectId,
        changes: () => ({ sections }),
      })),
      handleMessage({
        error: requestMessage('project_step_priority'),
      }),
    );
  };

  updateProjectTitle = (id: ProjectId, title: string) => {
    return ProjectAPI.updateTitle(id, title).pipe(this.projectRepo.ops.upsertOne(item => ({ item })));
  };

  orderTodos = (projectId: ProjectId, items: any[]) => {
    return ProjectAPI.orderTodos(projectId, items).pipe(this.projectRepo.ops.upsertOne(item => ({ item })));
  };

  addProposal = (projectId: ProjectId, ProposalTemplateId: ProposalTemplateId) => {
    return ProjectAPI.addProposal(projectId, ProposalTemplateId).pipe(this.projectRepo.ops.upsertOne(item => ({ item })));
  };

  createClickupTask = (request: ClickupTaskRequest, projectId: string, stepId: string) => {
    return ClickupAPI.createTask(request).pipe(
      handleMessage({
        type: requestMessage('sync_task_to_clickup'),
      }),
    );
  };

  unSyncClickupTask = (request: ClickupTaskRequest) => {
    return showDeleteConfirmation('Unsync', 'Do you really want to unsync this task from clickup?').pipe(
      switchMap(() => ClickupAPI.unSync(request)),
      handleMessage({
        type: requestMessage('unsync_task_from_clickup'),
      }),
    );
  };

  updateSectionClose = (projectId: ProjectId, sectionId: string, isClosed: boolean) => {
    return ProjectAPI.updateSectionClose(sectionId, isClosed).pipe(
      tap(() => {
        const project = this.projectRepo.getItem(projectId)! as ProjectResponse;
        const newSections = project.sections.map(sec => {
          if (sec.id === sectionId) {
            return { ...sec, isClosed };
          }
          return sec;
        });

        this.projectRepo.updateOne({
          id: projectId,
          changes: () => ({ sections: newSections }),
        });
      }),
    );
  };
}
